import { mulberry32 } from "../trailGeometry";

// Pure game logic for Connect the Dots — numbered blossoms float on a pond and
// the player joins them in order, 1, 2, 3…, as fast as they can. Everything
// here is side-effect free (randomness is injectable) so it can be unit tested.

// ---- Board ---------------------------------------------------------------
// The board fills the space it's given, from a tall phone to a wide monitor, so
// it is a rectangle, not a square. Board units: the SHORTER side is always 100
// units (the SVG viewBox is W x H with min(W, H) = 100), so blossoms are the
// same size relative to the screen whatever its shape — a wide screen simply
// gets more pond.
export const SHORT_SIDE = 100;
// Board proportions (width / height) are kept within this range: past it, an
// ultra-wide or very tall area gets a narrower or shorter board instead.
export const MIN_ASPECT = 0.6;
export const MAX_ASPECT = 2.4;
// On a short screen (a phone held sideways) the board keeps at least this
// height in px and the page scrolls.
export const MIN_BOARD_PX = 300;

// Fit the board (in px) into the space available: as big as possible, within
// MIN_ASPECT..MAX_ASPECT. Rounds down, so it never overshoots the space by a
// fraction of a pixel (which would bring back a scrollbar). Returns null when
// there's no space to measure (e.g. before layout), so callers can fall back
// to a square.
export const fitBoard = (availW, availH) => {
  if (!(availW > 0)) return null;
  let height = Math.max(MIN_BOARD_PX, availH);
  let width = Math.min(availW, height * MAX_ASPECT);
  if (width / height < MIN_ASPECT) height = Math.max(width / MIN_ASPECT, 1);
  return { width: Math.floor(width), height: Math.floor(height) };
};

// The viewBox size (board units) for a board of `width` x `height` px. Rounded
// to half units so small resizes don't relayout the scenery.
export const boardUnits = (width, height) => {
  const s = Math.min(width, height);
  const half = (v) => Math.round(v * 2) / 2;
  return { W: half((SHORT_SIDE * width) / s), H: half((SHORT_SIDE * height) / s) };
};

// ---- Blossoms --------------------------------------------------------------
// Blossom radius in board units: the tap target (FLOWER_R + 0.5) is ~12% of
// the board's short side across, about 40px even on a phone.
export const FLOWER_R = 5.5;
// Closest two blossoms may start, centre to centre: a little clear water
// between their petal tips.
export const MIN_SPACING = 2 * FLOWER_R + 2;

// ---- Pond ------------------------------------------------------------------
// The pond is an oval fitted to the board, its edge given a hand-cut wobble by a
// few low harmonics; the bank shows all round it. Blossom centres stay inside a
// smaller "open water" oval — convex, so a drifting blossom (which moves in a
// straight line between two points inside it) never leaves it either — far
// enough in that every petal stays afloat.
const SHORE_HARMONICS = [
  { k: 3, amp: 0.7, phase: 0.6 },
  { k: 5, amp: 0.42, phase: 2.1 },
  { k: 7, amp: 0.28, phase: 4.0 },
];
const WOBBLE = SHORE_HARMONICS.reduce((sum, h) => sum + h.amp, 0);
// Narrowest strip of bank between the shore and the board edge.
const BANK_MIN = 2.7;
// Clear water between a blossom's petals and the shore. A touch over 1 unit:
// two ovals a fixed distance apart along their axes come slightly closer than
// that between the axes.
const SHORE_CLEAR = 1.6;

// The pond for a W x H board: centre, the shore oval's semi-axes (sx, sy —
// before the wobble, which only ever pushes the shore outward) and the open
// water's semi-axes (ax, ay).
export const makePond = (W = SHORT_SIDE, H = SHORT_SIDE) => {
  const sx = W / 2 - BANK_MIN - 2 * WOBBLE;
  const sy = H / 2 - BANK_MIN - 2 * WOBBLE;
  return {
    W,
    H,
    cx: W / 2,
    cy: H / 2,
    sx,
    sy,
    ax: sx - FLOWER_R - SHORE_CLEAR,
    ay: sy - FLOWER_R - SHORE_CLEAR,
  };
};

// Defaults used when the player's level carries no (valid) parameters — the
// same as Level 1.
export const DEFAULT_PARAMS = { dotsCount: 3, movement: false };
const MOVEMENT_START_LEVEL = 2;

// Score = 100 minus the whole seconds taken since Start, never below 0. The
// level thresholds in core/data/game_levels.json are set against this.
export const MAX_SCORE = 100;

// Petal colours, one per blossom. Three reuse the home meadow's bloom classes
// (FlowerField.css); "lily" is the pond's own cream water-lily.
export const BLOOMS = ["marigold", "berry", "butter", "lily"];

const r2 = (v) => Math.round(v * 100) / 100;

// Whether the blossoms drift on this level. The level's own "movement" flag
// decides; levels without one fall back to the old rule (moving above level 2).
export const levelHasMovement = (params, levelNumber) => {
  if (params && typeof params.movement === "boolean") return params.movement;
  return levelNumber > MOVEMENT_START_LEVEL;
};

// Parse a level's parameters from its level_parameters JSON, e.g.
//   {"dotsCount": 7, "movement": true}
// A missing or invalid count falls back to DEFAULT_PARAMS; an absent level
// gets DEFAULT_PARAMS outright.
export const parseLevelParams = (level) => {
  if (!level) return { ...DEFAULT_PARAMS };
  let p = {};
  try {
    p = JSON.parse(level.level_parameters) || {};
  } catch (e) {
    p = {};
  }
  const n = p.dotsCount;
  return {
    dotsCount: Number.isInteger(n) && n >= 2 && n <= 15 ? n : DEFAULT_PARAMS.dotsCount,
    movement: levelHasMovement(p, level.level_number),
  };
};

// Distance from the pond centre to the shoreline at angle `theta` (radians,
// screen coordinates): the shore oval's radius there, pushed out by 0..2×WOBBLE.
export const shoreRadius = (pond, theta) => {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const oval = (pond.sx * pond.sy) / Math.hypot(pond.sy * c, pond.sx * s);
  return SHORE_HARMONICS.reduce(
    (r, h) => r + h.amp * Math.sin(h.k * theta + h.phase),
    oval + WOBBLE,
  );
};

// Whether a board point is on dry land, at least `margin` past the shoreline.
export const onBank = (pond, x, y, margin = 0) => {
  const dx = x - pond.cx;
  const dy = y - pond.cy;
  return Math.hypot(dx, dy) >= shoreRadius(pond, Math.atan2(dy, dx)) + margin;
};

// Whether a point is on the open water blossoms float on.
export const inOpenWater = (pond, p) =>
  ((p.x - pond.cx) / pond.ax) ** 2 + ((p.y - pond.cy) / pond.ay) ** 2 <= 1 + 1e-9;

// Carry a point from one pond to another (the board changed shape mid-round):
// it keeps its place relative to the open water — the same fraction of the way
// out from the centre along each axis — so anything afloat stays afloat.
export const remapPoint = (from, to) => (p) => ({
  x: to.cx + ((p.x - from.cx) * to.ax) / from.ax,
  y: to.cy + ((p.y - from.cy) * to.ay) / from.ay,
});

// Pull a point back onto the open water, towards the centre, if it's off it.
const ontoOpenWater = (pond, p) => {
  const e = ((p.x - pond.cx) / pond.ax) ** 2 + ((p.y - pond.cy) / pond.ay) ** 2;
  if (e <= 1) return p;
  const k = 1 / Math.sqrt(e);
  return { x: pond.cx + (p.x - pond.cx) * k, y: pond.cy + (p.y - pond.cy) * k };
};

// Nudge points apart until each is at least `spacing` from every other,
// keeping them on the open water. Remapping to a pond squeezed along one axis
// (a window narrowed, or a phone turned, mid-round) can bring two still
// blossoms together; this separates them while leaving well-spaced ones put.
export const spreadApart = (pond, points, spacing = MIN_SPACING, rounds = 100) => {
  const pts = points.map((p) => ({ ...p }));
  for (let round = 0; round < rounds; round++) {
    let moved = false;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[j].x - pts[i].x;
        const dy = pts[j].y - pts[i].y;
        const d = Math.hypot(dx, dy);
        if (d >= spacing) continue;
        // Each moves half the shortfall (a hair more, so it settles), directly
        // away from the other; two on the same spot split sideways.
        const push = (spacing - d) / 2 + 1e-3;
        const ux = d > 0 ? dx / d : 1;
        const uy = d > 0 ? dy / d : 0;
        pts[i] = ontoOpenWater(pond, { x: pts[i].x - ux * push, y: pts[i].y - uy * push });
        pts[j] = ontoOpenWater(pond, { x: pts[j].x + ux * push, y: pts[j].y + uy * push });
        moved = true;
      }
    }
    if (!moved) break;
  }
  return pts;
};

// The shoreline as a smooth closed SVG path: sample points around the shore,
// then run quadratic curves through the midpoints between them.
export const pondPath = (pond, samples = 160) => {
  const pts = [];
  for (let i = 0; i < samples; i++) {
    const a = (i / samples) * Math.PI * 2;
    const r = shoreRadius(pond, a);
    pts.push([pond.cx + r * Math.cos(a), pond.cy + r * Math.sin(a)]);
  }
  const mid = (p, q) => `${r2((p[0] + q[0]) / 2)},${r2((p[1] + q[1]) / 2)}`;
  let d = `M${mid(pts[samples - 1], pts[0])}`;
  for (let i = 0; i < samples; i++) {
    const p = pts[i];
    d += ` Q${r2(p[0])},${r2(p[1])} ${mid(p, pts[(i + 1) % samples])}`;
  }
  return `${d} Z`;
};

// A uniformly random point on the open water (a uniform point in the unit
// disc, stretched onto the oval).
const randomOnPond = (pond, rng) => {
  const r = Math.sqrt(rng());
  const a = rng() * Math.PI * 2;
  return { x: pond.cx + pond.ax * r * Math.cos(a), y: pond.cy + pond.ay * r * Math.sin(a) };
};

// One rejection-sampling pass: returns `count` points at least `spacing`
// apart, or null if it ran out of tries.
const tryScatter = (pond, count, rng, spacing) => {
  const min2 = spacing * spacing;
  const pts = [];
  for (let tries = 0; pts.length < count && tries < count * 200; tries++) {
    const p = randomOnPond(pond, rng);
    if (pts.every((q) => (q.x - p.x) ** 2 + (q.y - p.y) ** 2 >= min2)) pts.push(p);
  }
  return pts.length === count ? pts : null;
};

// Scatter `count` points at random over the open water, each at least
// `spacing` from every other. Positions are fresh every game — only overlaps
// are ruled out. If a crowded board can't fit (unlikely at the counts the
// levels use), the spacing relaxes a little at a time until it does.
export const scatterSpaced = (pond, count, rng = Math.random, spacing = MIN_SPACING) => {
  for (let s = spacing; s >= 1; s *= 0.9) {
    for (let attempt = 0; attempt < 10; attempt++) {
      const pts = tryScatter(pond, count, rng, s);
      if (pts) return pts;
    }
  }
  return Array.from({ length: count }, () => randomOnPond(pond, rng));
};

// Lay out a game's blossoms: id (0-based; shown as id + 1), a start point and a
// drift target (the same point when the level doesn't move), plus the look of
// each bloom: colour, petal count and how far its petal ring is turned.
export const makeBlossoms = (pond, count, movement, rng = Math.random) => {
  const starts = scatterSpaced(pond, count, rng);
  const targets = movement ? scatterSpaced(pond, count, rng) : starts;
  return starts.map((start, id) => ({
    id,
    start,
    target: targets[id],
    bloom: BLOOMS[Math.floor(rng() * BLOOMS.length)],
    petals: 6 + Math.floor(rng() * 3),
    turn: Math.round(rng() * 360),
  }));
};

// A random drawing order for `count` blossoms (Fisher-Yates). Drawing them in
// id order would also make Tab visit them 1, 2, 3… — giving the answer away to
// keyboard players — and always stack higher numbers on top.
export const drawOrder = (count, rng = Math.random) => {
  const order = Array.from({ length: count }, (_, i) => i);
  for (let i = count - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
};

// Drift speed in board units per second. `speed` is the old per-step interval
// in ms (default 50), which moved the dots about a tenth of the board a second.
export const driftSpeed = (speed) => 500 / speed;

// Where a blossom is at `phase` along its start→target line (0 = start).
export const positionAt = (blossom, phase) => ({
  x: blossom.start.x + (blossom.target.x - blossom.start.x) * phase,
  y: blossom.start.y + (blossom.target.y - blossom.start.y) * phase,
});

// Move a blossom's drift ({phase 0..1, dir ±1}) on by `dt` seconds at
// `unitsPerSec`, turning back at either end of its line.
export const advanceDrift = (blossom, drift, unitsPerSec, dt) => {
  const len = Math.hypot(
    blossom.target.x - blossom.start.x,
    blossom.target.y - blossom.start.y,
  );
  if (len === 0) return drift;
  const phase = drift.phase + drift.dir * (unitsPerSec / len) * dt;
  if (phase >= 1) return { phase: 1, dir: -1 };
  if (phase <= 0) return { phase: 0, dir: 1 };
  return { phase, dir: drift.dir };
};

// What tapping blossom `id` does when the first `joined` blossoms are already
// connected: "join" the next one, "undo" the most recent one, "wrong" for any
// blossom not yet reached, and "ignore" for earlier, already-joined ones.
export const tapResult = (id, joined) => {
  if (id === joined) return "join";
  if (id === joined - 1) return "undo";
  return id > joined ? "wrong" : "ignore";
};

export const scoreAfter = (elapsedMs) =>
  Math.max(0, MAX_SCORE - Math.floor(elapsedMs / 1000));

// ---- Bank scenery ----------------------------------------------------------
// Grass tufts and cattail clumps on the dry land around the pond, from a fixed
// seed so a given board size always looks the same. Deliberately no flowers:
// the only flowers on the board are the ones to tap. Nothing here is
// interactive; it only has to stay on land.

const BANK_SEED = 0x90d;
const BANK_STEP = 6.5; // scatter grid spacing
const REED_HEIGHT = 11;

const sceneryCache = new Map();
export const bankScenery = (pond) => {
  const key = `${pond.W}x${pond.H}`;
  if (sceneryCache.has(key)) return sceneryCache.get(key);
  const { W, H } = pond;
  const rand = mulberry32(BANK_SEED);
  const tufts = [];
  for (let gy = 0; gy < Math.ceil(H / BANK_STEP); gy++) {
    for (let gx = 0; gx < Math.ceil(W / BANK_STEP); gx++) {
      // A fixed number of draws per cell keeps the scatter stable.
      const x = r2((gx + 0.2 + rand() * 0.6) * BANK_STEP);
      const y = r2((gy + 0.2 + rand() * 0.6) * BANK_STEP);
      const kind = rand();
      const flip = rand() < 0.5 ? -1 : 1;
      if (x < 2 || x > W - 2 || y < 4 || y > H - 1 || !onBank(pond, x, y, 1.5)) continue;
      if (kind < 0.55) tufts.push({ x, y, flip, dark: kind < 0.3 });
    }
  }
  // Cattail clumps sit in three of the roomy corners (ground points).
  const reeds = [
    [11, 15],
    [W - 14, 13],
    [W - 10, H - 8],
  ]
    .filter(([x, y]) => onBank(pond, x, y, 0.5) && onBank(pond, x, y - REED_HEIGHT, 1))
    .map(([x, y]) => ({ x, y }));
  const scenery = { tufts, reeds, reedHeight: REED_HEIGHT };
  if (sceneryCache.size > 24) sceneryCache.clear();
  sceneryCache.set(key, scenery);
  return scenery;
};
